import React, { useState, useRef } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  Tooltip
} from 'recharts';

// 파스텔 톤 난이도 색상 (Lv1~Lv5)
// 기존 LearningStatus/utils/constants.js 의 DIFFICULTY_COLORS와 일치
const DIFFICULTY_COLORS = [
  'rgba(253, 224, 71, 0.7)',   // Lv1 yellow-300
  'rgba(134, 239, 172, 0.7)',  // Lv2 green-300
  'rgba(147, 197, 253, 0.7)',  // Lv3 blue-300
  'rgba(253, 186, 116, 0.7)',  // Lv4 orange-300
  'rgba(252, 165, 165, 0.7)'   // Lv5 red-300
];

// UI 색상 토큰
const COLORS = {
  primary: '#4ABC85',
  blue500: '#3B82F6',
  danger: '#DC2626',
  headerBg: '#231F20',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  white: '#FFFFFF',
  tooltipBorder: '#CCCCCC',
  shadowBlack: 'rgba(0,0,0,0.1)'
};

// 난이도별 진한 컬러(레이팅 dot/active-dot 및 왼쪽 Y축 레이블 색)
const DIFFICULTY_COLORS_OPAQUE = ['#EAB308', '#059669', '#2563EB', '#EA580C', '#DC2626'];

// 레이팅에 따른 색상
function getRatingColor(rating) {
  if (!rating && rating !== 0) return COLORS.gray400;
  if (rating <= 2000) return DIFFICULTY_COLORS_OPAQUE[0];
  if (rating <= 4000) return DIFFICULTY_COLORS_OPAQUE[1];
  if (rating <= 6000) return DIFFICULTY_COLORS_OPAQUE[2];
  if (rating <= 8000) return DIFFICULTY_COLORS_OPAQUE[3];
  return DIFFICULTY_COLORS_OPAQUE[4];
}

/**
 * props:
 * - selectedTest: { exam_id, stamp_id, tree_name? }
 * - testHistory: Test[] (아래 계약 참고)
 * - reportData: { exams: { exam_id, rating, delta }[] }
 * - onTestSelect?: (test) => void
 */
const FormRatingTrendChart = React.memo(function FormRatingTrendChart({
  selectedTest,
  testHistory,
  reportData,
  onTestSelect,
  customTitle
}) {
  const [tooltipActive, setTooltipActive] = React.useState(false);
  const [activeChartIndex, setActiveChartIndex] = React.useState(0);
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchEnd, setTouchEnd] = React.useState(null);
  const stackInfoRef = React.useRef({});
  
  // Clear stack info when switching charts
  React.useEffect(() => {
    stackInfoRef.current = {};
  }, [activeChartIndex]);

  // Group tests by stamp_id and sort by most recent date
  const groupedChartData = React.useMemo(() => {
    if (!testHistory || testHistory.length === 0) return [];

    // Group tests by stamp_id
    const stampGroups = {};
    testHistory.forEach(test => {
      if (test.exam_status === 999 && test.stamp_id) {
        if (!stampGroups[test.stamp_id]) {
          stampGroups[test.stamp_id] = {
            stamp_id: test.stamp_id,
            tree_name: test.tree_name || '폼레이팅',
            tests: [],
            mostRecentDate: null
          };
        }
        stampGroups[test.stamp_id].tests.push(test);
        const testDate = new Date(test.created_at || test.exam_created_at || test.updated_at);
        if (!stampGroups[test.stamp_id].mostRecentDate || testDate > stampGroups[test.stamp_id].mostRecentDate) {
          stampGroups[test.stamp_id].mostRecentDate = testDate;
        }
      }
    });

    // Sort stamp groups by most recent date (newest first)
    const sortedGroups = Object.values(stampGroups).sort((a, b) => {
      return b.mostRecentDate - a.mostRecentDate;
    });

    const reportMap = new Map(
      (reportData?.exams || []).map(e => [String(e.exam_id), e])
    );

    // Process each stamp group
    return sortedGroups.map(group => {
      const sorted = [...group.tests].sort((a, b) => 
        new Date(a.created_at || a.exam_created_at || a.updated_at) - 
        new Date(b.created_at || b.exam_created_at || b.updated_at)
      );

      const rows = [];
      let xIndex = 1;

      for (const exam of sorted) {
      const totalQuestions = (exam.correct_count || 0) + (exam.wrong_count || 0);
      const accuracy = totalQuestions > 0 ? Math.round(((exam.correct_count || 0) / totalQuestions) * 100) : 0;

      // 난이도별 정답 분포/총 문항
      const correctByLevel = [0, 0, 0, 0, 0];
      const totalByLevel = [0, 0, 0, 0, 0];

      if (exam.by_step) {
        for (let level = 1; level <= 5; level++) {
          const stepData = exam.by_step[String(level)];
          if (Array.isArray(stepData) && stepData.length >= 2) {
            const [totalProblems, correctCount] = stepData;
            const idx = level - 1;
            totalByLevel[idx] = totalProblems || 0;
            correctByLevel[idx] = correctCount || 0;
          }
        }
      } else if (Array.isArray(exam.problems_list) && exam.problems_list.length > 0) {
        const resultMap = new Map(Object.entries(exam.result || {}).map(([pid, v]) => [parseInt(pid, 10), v]));
        for (const p of exam.problems_list) {
          const step = p.step;
          if (step >= 1 && step <= 5) {
            const idx = step - 1;
            totalByLevel[idx]++;
            if (resultMap.get(p.problem_id)) correctByLevel[idx]++;
          }
        }
      } else if (Array.isArray(exam.sequence)) {
        for (const item of exam.sequence) {
          if (Array.isArray(item)) {
            const step = item[1];
            const isCorrect = item[6];
            if (step >= 1 && step <= 5) {
              const idx = step - 1;
              totalByLevel[idx]++;
              if (isCorrect) correctByLevel[idx]++;
            }
          }
        }
      }

      const difficultyRatios = correctByLevel.map((count, idx) => {
        const total = totalByLevel[idx] || 0;
        return totalQuestions > 0 ? (count / totalQuestions) * 100 : 0;
      });

      // rating 우선순위: reportData.exams -> exam.rating
      const reportExam = reportMap.get(String(exam.exam_id));
      let rating = reportExam?.rating ?? null;
      let delta = reportExam?.delta ?? 0;

      if (rating === null && exam.rating && exam.rating !== 1000) {
        rating = exam.rating;
        delta = exam.delta || 0;
      }

      const prev = rows.length > 0 ? rows[rows.length - 1] : null;
      const prevRating = prev ? prev.rating : null;
      const ratingChange = prevRating != null && rating != null ? rating - prevRating : 0;
      const ratingChangePercent = prevRating != null && rating != null ? Number((((rating - prevRating) / prevRating) * 100).toFixed(2)) : 0;

      rows.push({
        x: xIndex,
        exam_round: exam.exam_round || xIndex,
        exam_id: exam.exam_id,
        stamp_id: exam.stamp_id,
        rating: rating ?? null,
        prevRating,
        ratingChange,
        ratingChangePercent,
        accuracy,
        delta,
        difficulty: exam.difficulty ?? 2.5,
        // Add minimum value (0.1) if all answers are wrong to ensure bar visibility
        level1: difficultyRatios[0] || (totalQuestions > 0 && accuracy === 0 ? 0.1 : 0),
        level2: difficultyRatios[1],
        level3: difficultyRatios[2],
        level4: difficultyRatios[3],
        level5: difficultyRatios[4],
        correctByLevel,
        totalByLevel,
        totalCorrect: correctByLevel.reduce((s, c) => s + c, 0),
        totalQuestions,
        testDate: exam.created_at || exam.exam_created_at || exam.updated_at,
        exam_type: exam.exam_type || 'unknown',
        tree_name: exam.tree_name || '',
        hasRating: rating !== null
      });

        xIndex++;
      }

      // 최소 10회차 보장(빈 데이터 패딩)
      if (rows.length < 10) {
        const padded = [];
        for (let i = 1; i <= 10; i++) {
          const found = rows.find(r => r.exam_round === i);
          if (found) padded.push(found);
          else {
            padded.push({
              x: i,
              exam_round: i,
              rating: null,
              accuracy: null,
              level1: 0,
              level2: 0,
              level3: 0,
              level4: 0,
              level5: 0,
              hasRating: false
            });
          }
        }
        return {
          stamp_id: group.stamp_id,
          tree_name: group.tree_name,
          data: padded
        };
      }

      return {
        stamp_id: group.stamp_id,
        tree_name: group.tree_name,
        data: rows
      };
    });
  }, [testHistory, reportData]);

  // Swipe handlers
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      // Swipe left = go to next (with wrap around)
      setActiveChartIndex((prev) => 
        prev === groupedChartData.length - 1 ? 0 : prev + 1
      );
    }
    if (isRightSwipe) {
      // Swipe right = go to previous (with wrap around)
      setActiveChartIndex((prev) => 
        prev === 0 ? groupedChartData.length - 1 : prev - 1
      );
    }
  };

  const labelFormatter = React.useCallback((label, payload) => {
    if (!payload?.[0]?.payload) return '';
    const d = payload[0].payload;
    const accuracy = d.accuracy || 0;
    const hasData = d.rating != null;
    return (
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
          {d.exam_round}회차 {d.exam_id ? `(TID: ${d.exam_id})` : ''}
        </div>
        <div style={{ fontSize: 10, color: COLORS.gray500, marginBottom: 2 }}>
          {d.testDate ? new Date(d.testDate).toLocaleDateString('ko-KR') : ''}
        </div>
        <div style={{ color: COLORS.primary, fontWeight: 'bold', marginTop: 2 }}>
          {hasData ? `정답률: ${accuracy}%` : '미 실시'}
        </div>
      </div>
    );
  }, []);

  const formatter = React.useCallback((value, name, props) => {
    if (!props?.payload) return [`${value}`, name];

    if (name === 'rating') {
      if (value == null) return null;
      const { prevRating, ratingChange, ratingChangePercent, difficulty, totalQuestions, exam_type } = props.payload;
      const changeColor = ratingChange > 0 ? '#EF4444' : ratingChange < 0 ? COLORS.blue500 : COLORS.gray500;
      const changeSymbol = ratingChange > 0 ? '▲' : ratingChange < 0 ? '▼' : '';

      return [
        <div>
          <div style={{ color: COLORS.blue500, fontWeight: 'bold' }}>
            폼레이팅: {Number(value).toLocaleString()}
          </div>
          {prevRating != null && (
            <>
              <div style={{ marginLeft: 12, color: COLORS.headerBg, marginTop: 2, fontSize: 10 }}>
                이전 점수: {Number(prevRating).toLocaleString()}
              </div>
              <div style={{ marginLeft: 12, color: COLORS.headerBg, marginTop: 2 }}>
                전 회차 대비:{' '}
                {ratingChange !== 0 ? (
                  <span style={{ color: changeColor, fontSize: 10 }}>
                    {changeSymbol}{Math.abs(ratingChange)} ({ratingChangePercent > 0 ? '+' : ''}{ratingChangePercent}%)
                  </span>
                ) : (
                  <span style={{ color: COLORS.gray500, fontSize: 10 }}>변화 없음</span>
                )}
              </div>
            </>
          )}
          {exam_type && (
            <div style={{ marginLeft: 12, color: COLORS.headerBg, marginTop: 2, fontSize: 10 }}>
              시험 타입: {String(exam_type).toUpperCase()}
            </div>
          )}
          {totalQuestions != null && (
            <div style={{ marginLeft: 12, color: COLORS.headerBg, marginTop: 2, fontSize: 10 }}>
              문제 수: {totalQuestions}개
            </div>
          )}
          {difficulty != null && (
            <div style={{ marginLeft: 12, color: COLORS.headerBg, marginTop: 2, fontSize: 10 }}>
              평균 난이도: {Number(difficulty).toFixed(1)}
            </div>
          )}
        </div>,
        ''
      ];
    }

    if (name.startsWith('Level')) {
      if (!value) return [null, ''];
      const { correctByLevel, totalByLevel } = props.payload;
      if (!correctByLevel || !totalByLevel) return [null, ''];
      const idx = parseInt(name.split(' ')[1], 10) - 1;
      const count = correctByLevel[idx] || 0;
      const total = totalByLevel[idx] || 0;
      const levelAccuracy = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
      const levelColor = DIFFICULTY_COLORS[idx];

      return [
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 12 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, backgroundColor: levelColor, borderRadius: 1 }} />
          <span style={{ color: COLORS.headerBg }}>
            Lv{idx + 1}: {count}/{total}개 ({levelAccuracy}%)
          </span>
        </div>,
        ''
      ];
    }

    return [`${value}%`, name];
  }, []);

  if (groupedChartData.length === 0) {
    return (
      <div className="relative rounded-3xl bg-white p-4 shadow-lg">
        <div className="flex h-44 items-center justify-center">
          <p className="text-sm text-gray-500">테스트 데이터가 없습니다</p>
        </div>
      </div>
    );
  }


  return (
    <div className="w-full">
      {/* Active Chart without Swipe */}
      {groupedChartData[activeChartIndex] && (
        <div 
          className="rounded-3xl bg-white p-4 shadow-sm transition-all"
          style={{ 
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)'
          }}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-800">
              {customTitle || groupedChartData[activeChartIndex].tree_name}
            </h3>
          </div>

          {/* 범례 */}
          <div className="mb-3 flex items-start justify-between">
            <div className="flex items-center gap-1">
              <div className="h-2 w-3 rounded-3xl bg-blue-500" />
              <span className="text-[10px] font-medium text-gray-600">폼레이팅</span>
            </div>
            <div className="flex items-center gap-3">
              {[1,2,3,4,5].map((level, i) => (
                <div key={level} className="flex items-center gap-1">
                  <div className="h-2 w-3 rounded-3xl" style={{ backgroundColor: DIFFICULTY_COLORS[i] }} />
                  <span className="text-[9px] text-gray-600">Lv{level}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="h-32">
            {groupedChartData[activeChartIndex].data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={groupedChartData[activeChartIndex].data}
              margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
              onMouseEnter={() => setTooltipActive(true)}
              onMouseMove={() => setTooltipActive(true)}
              onMouseLeave={() => setTooltipActive(false)}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gray100} />
              <XAxis
                dataKey="exam_round"
                axisLine={false}
                tickLine={false}
                interval={0}
                minTickGap={10}
                dy={0}
                tickMargin={4}
                  tick={(props) => {
                    const { x, y, payload, index } = props;
                    const dataPoint = groupedChartData[activeChartIndex].data.find(item => item.exam_round === payload.value);
                  const isSelected = selectedTest && dataPoint && dataPoint.exam_id === selectedTest.exam_id;

                  // Calculate if we should show this label
                  const totalLabels = groupedChartData[activeChartIndex].data.length;
                  const skipInterval = totalLabels > 15 ? 3 : totalLabels > 10 ? 2 : 1;
                  const shouldShow = isSelected || index % skipInterval === 0 || index === totalLabels - 1;

                  if (!shouldShow) return null;

                  if (isSelected) {
                    return (
                      <g>
                        <text x={x} y={y} dy={5} textAnchor="middle" fontSize={14} fill={COLORS.primary} fontWeight="bold">
                          {payload.value}
                        </text>
                        <text x={x} y={y} dy={15} textAnchor="middle" fontSize={7} fill={COLORS.primary} fontWeight="bold">
                          회차
                        </text>
                      </g>
                    );
                  }
                  // Check if this x-label has data
                  const hasData = groupedChartData[activeChartIndex].data.some(d => d.exam_round === payload.value && d.rating != null);
                  return (
                    <text x={x} y={y} dy={5} textAnchor="middle" fontWeight="bold" fontSize={10} fill={hasData ? COLORS.gray400 : COLORS.gray300}>
                      {payload.value}
                    </text>
                  );
                }}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                domain={[0, 10000]}
                axisLine={false}
                tickLine={false}
                ticks={[0, 2000, 4000, 6000, 8000, 10000]}
                tickCount={6}
                interval={0}
                width={32}
                tickMargin={-5}
                tick={(props) => {
                  const { x, y, payload } = props;
                  const value = payload.value;
                  const color = getRatingColor(value);
                  const isBold = [2000, 4000, 6000, 8000].includes(value);
                  return (
                    <text x={x} y={y} textAnchor="end" fontSize={8} fill={color} fontWeight={isBold ? 'bolder' : 'bold'} dx={-2}>
                      {Number(value).toLocaleString()}
                    </text>
                  );
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 9, fill: COLORS.primary, fontWeight: 'bold' }}
                width={20}
                tickMargin={-5}
              />
              <Tooltip
                active={tooltipActive}
                onMouseEnter={() => setTooltipActive(true)}
                onMouseLeave={() => setTooltipActive(false)}
                filterNull
                contentStyle={{
                  fontSize: '11px',
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: `1px solid ${COLORS.tooltipBorder}`,
                  backgroundColor: COLORS.white,
                  boxShadow: `0 2px 8px ${COLORS.shadowBlack}`,
                  position: 'relative',
                  transform: 'translateY(120px)',
                  lineHeight: 1.2
                }}
                wrapperStyle={{ zIndex: 10000 }}
                itemStyle={{ color: COLORS.headerBg, margin: 0, padding: 0, lineHeight: 1 }}
                separator=""
                allowEscapeViewBox={{ x: false, y: false }}
                offset={0}
                isAnimationActive={false}
                labelFormatter={labelFormatter}
                formatter={formatter}
              />
              {/* Render stacked bars with mask approach */}
              {[0,1,2,3,4].map((i) => (
                <Bar
                  key={i}
                  yAxisId="right"
                  dataKey={`level${i + 1}`}
                  stackId="difficulty"
                  fill={DIFFICULTY_COLORS[i]}
                  fillOpacity={0.7}
                  stroke="none"
                  strokeWidth={0}
                  name={`Level ${i + 1}`}
                  legendType="none"
                  isAnimationActive={false}
                  shape={(props) => {
                    const { x, y, width, height, payload, index } = props;
                    const isSelected = selectedTest && payload.exam_id === selectedTest.exam_id;
                    
                    // Calculate stack info for mask
                    const levels = [payload.level1, payload.level2, payload.level3, payload.level4, payload.level5];
                    const totalValue = levels.reduce((sum, val) => sum + (val || 0), 0);
                    
                    // Skip if no data at all
                    if (totalValue === 0) return null;
                    
                    // Use consistent mask ID based on index and chart
                    const maskId = `mask-${index}-${activeChartIndex}`;
                    const stackKey = `stack-${index}-${activeChartIndex}`;
                    
                    // Calculate and cache stack info only once per stack
                    let stackInfo = stackInfoRef.current[stackKey];
                    
                    if (!stackInfo && height > 0) {
                      // We have data for this bar, calculate stack info
                      const currentValue = levels[i];
                      if (currentValue > 0) {
                        const beforeValue = levels.slice(0, i).reduce((sum, val) => sum + (val || 0), 0);
                        
                        // Calculate the full stack dimensions
                        const pixelsPerUnit = height / currentValue;
                        const stackHeight = totalValue * pixelsPerUnit;
                        const beforeHeight = beforeValue * pixelsPerUnit;
                        
                        // Stack bounds
                        const stackBottom = y + height + beforeHeight;
                        const stackTop = stackBottom - stackHeight;
                        
                        // Cache the stack info
                        stackInfoRef.current[stackKey] = {
                          x,
                          y: stackTop,
                          width,
                          height: stackHeight,
                          definedAt: i  // Remember which bar defined the mask
                        };
                        
                        stackInfo = stackInfoRef.current[stackKey];
                      }
                    }
                    
                    // Skip rendering if no height for this specific bar
                    if (height <= 0) return null;
                    
                    // If we still don't have stack info, this bar can't render
                    if (!stackInfo) return null;
                    
                    const radius = Math.min(16, width / 2);
                    
                    // Only render mask definition at the bar that calculated it
                    const shouldRenderMask = stackInfo.definedAt === i;
                    
                    return (
                      <g>
                        {/* Selection highlight - render once per stack at the first bar that has data */}
                        {shouldRenderMask && isSelected && (
                          <rect
                            x={x - width * 0.2}
                            y={0}
                            width={width * 1.4}
                            height={176}
                            fill="rgba(74, 188, 133, 0.08)"
                            rx={6}
                            ry={6}
                          />
                        )}
                        
                        {/* Define mask for the entire stack */}
                        {shouldRenderMask && (
                          <defs>
                            <mask id={maskId}>
                              <rect
                                x={stackInfo.x}
                                y={stackInfo.y}
                                width={stackInfo.width}
                                height={stackInfo.height}
                                rx={radius}
                                ry={radius}
                                fill="white"
                              />
                            </mask>
                          </defs>
                        )}
                        
                        {/* Bar segment with mask */}
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={DIFFICULTY_COLORS[i]}
                          fillOpacity={0.7}
                          mask={`url(#${maskId})`}
                          style={{ cursor: payload.exam_id ? 'pointer' : 'default' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (payload.exam_id && onTestSelect) {
                              onTestSelect(payload);
                            }
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            if (payload.exam_id && onTestSelect) {
                              onTestSelect(payload);
                            }
                          }}
                        />
                        
                        {/* Click area - render once per stack at the first bar that has data */}
                        {shouldRenderMask && payload.exam_id && (
                          <rect
                            x={x - width * 0.2}
                            y={0}
                            width={width * 1.4}
                            height={176}
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onTestSelect?.(payload);
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              onTestSelect?.(payload);
                            }}
                          />
                        )}
                      </g>
                    );
                  }}
                />
              ))}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="rating"
                stroke="rgba(59, 130, 246, 0.7)"
                strokeWidth={2}
                isAnimationActive={false}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  const rating = payload?.rating;
                  if (rating == null) return null;
                  const color = getRatingColor(rating);
                  return <circle cx={cx} cy={cy} r={3} fill={color} stroke={COLORS.white} strokeWidth={2} />;
                }}
                activeDot={(props) => {
                  const { cx, cy, payload } = props;
                  const rating = payload?.rating;
                  if (rating == null) return null;
                  const color = getRatingColor(rating);
                  return <circle cx={cx} cy={cy} r={5} fill={color} stroke={COLORS.white} strokeWidth={2} />;
                }}
                name="rating"
                legendType="none"
              />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-gray-500">폼레이팅 데이터가 없습니다</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Dot Navigation Controls with Swipe */}
      {groupedChartData.length > 1 && (
        <div 
          className="flex items-center justify-center gap-2 py-2"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {groupedChartData.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveChartIndex(index)}
              className={`transition-all rounded-full ${
                index === activeChartIndex 
                  ? 'w-3 h-3 bg-[#4ABC85]' 
                  : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to chart ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export default FormRatingTrendChart;


